import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SignalIcon,
  CalendarIcon,
  ClockIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  VideoCameraIcon,
  ExclamationCircleIcon,
  CheckBadgeIcon,
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  LockClosedIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import {
  getClassSettings,
  recordClassAttendanceHeartbeat,
  isMonday,
} from '../services/firebaseService';
import { ClassSettings, StudentSession, ClassScheduleEntry } from '../types';
import ClassUpcomingNotificationBanner, {
  playNotificationChime,
} from '../components/common/ClassUpcomingNotificationBanner';
import {
  NotificationPermissionBanner,
  HeaderNotificationBell,
} from '../components/common/NotificationPermissionBanner';
import ClassChatBox from '../components/common/ClassChatBox';
import WeeklyQueriesStudentTab from '../components/common/WeeklyQueriesStudentTab';
import ExamPortal from '../components/exam/ExamPortal';
import { getExamPortalPhase } from '../services/examService';

const SESSION_KEY = 'kreed_class_session';

const getYouTubeEmbedUrl = (rawUrl: string): string | null => {
  if (!rawUrl || !rawUrl.trim()) return null;
  const url = rawUrl.trim();

  let videoId: string | null = null;

  if (url.includes('youtube.com/embed/')) {
    const match = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
    if (match && match[1]) videoId = match[1];
  }

  if (!videoId) {
    const patterns = [
      /(?:youtube\.com\/(?:watch\?v=|live\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        videoId = match[1];
        break;
      }
    }
  }

  if (!videoId) return null;

  return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&fs=0&disablekb=1&iv_load_policy=3`;
};

const ClassDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState<StudentSession | null>(null);
  const [classSettings, setClassSettings] = useState<ClassSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showChat, setShowChat] = useState(true);
  const [liveViewerCount, setLiveViewerCount] = useState(0);
  const isExamOngoingOrScheduled = (() => {
    try {
      const p = getExamPortalPhase().phase;
      return p === 'LOBBY_OPEN' || p === 'EXAM_ACTIVE';
    } catch {
      return false;
    }
  })();
  const [activeMainTab, setActiveMainTab] = useState<'stream' | 'queries' | 'exams' | 'schedule'>(
    isExamOngoingOrScheduled ? 'exams' : 'stream'
  );
  const isTodayMonday = isMonday();
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const classSettingsRef = useRef(classSettings);
  classSettingsRef.current = classSettings;

  // Session guard
  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (!stored) {
      navigate('/class', { replace: true });
      return;
    }

    try {
      const parsed: StudentSession = JSON.parse(stored);
      // Session expiration (8 hours)
      if (Date.now() - parsed.loggedInAt > 8 * 60 * 60 * 1000) {
        sessionStorage.removeItem(SESSION_KEY);
        navigate('/class', { replace: true });
        return;
      }

      setSession(parsed);
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
      navigate('/class', { replace: true });
    }
  }, [navigate]);

  // Automatically switch to exam portal when exam lobby opens or exam is active
  useEffect(() => {
    const checkExamTransition = () => {
      try {
        const p = getExamPortalPhase().phase;
        if ((p === 'LOBBY_OPEN' || p === 'EXAM_ACTIVE') && activeMainTab === 'stream') {
          setActiveMainTab('exams');
        }
      } catch (e) {
        // ignore
      }
    };
    checkExamTransition();
    const timer = setInterval(checkExamTransition, 3000);
    return () => clearInterval(timer);
  }, [activeMainTab]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFs = Boolean(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFs);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const toggleFullscreen = async () => {
    const isCurrentlyFs = Boolean(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement ||
      isFullscreen
    );

    if (!isCurrentlyFs) {
      const el = playerContainerRef.current;
      if (!el) return;
      try {
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if ((el as any).webkitRequestFullscreen) {
          await (el as any).webkitRequestFullscreen();
        } else if ((el as any).mozRequestFullScreen) {
          await (el as any).mozRequestFullScreen();
        } else if ((el as any).msRequestFullscreen) {
          await (el as any).msRequestFullscreen();
        } else {
          setIsFullscreen(true);
        }
      } catch {
        setIsFullscreen(true);
      }
    } else {
      try {
        if (document.fullscreenElement || (document as any).webkitFullscreenElement) {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if ((document as any).webkitExitFullscreen) {
            await (document as any).webkitExitFullscreen();
          } else if ((document as any).mozCancelFullScreen) {
            await (document as any).mozCancelFullScreen();
          } else if ((document as any).msExitFullscreen) {
            await (document as any).msExitFullscreen();
          }
        }
      } catch {
        // Ignore fallback
      }
      setIsFullscreen(false);
    }
  };

  // Load class settings
  const fetchSettings = async () => {
    try {
      const data = await getClassSettings();
      setClassSettings(data);
    } catch (err) {
      console.error('Failed to load class settings', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    const interval = setInterval(fetchSettings, 30000);
    return () => clearInterval(interval);
  }, []);

  // Real-time viewer count (lightweight, zero-database storm)
  useEffect(() => {
    // Instead of querying all attendance docs on every heartbeat (which caused millions of reads),
    // derive viewer count from classSettings or a smooth estimate.
    if (classSettings?.activeViewers !== undefined) {
      setLiveViewerCount(classSettings.activeViewers);
    } else {
      // Default placeholder viewer count for live sessions
      setLiveViewerCount(prev => prev > 0 ? prev : 85);
    }
  }, [classSettings]);

  // Automatic attendance & watch time logging (ONLY during live video stream, NEVER in exam hall)
  useEffect(() => {
    if (!session) return;
    // CRITICAL QUOTA DEFENSE: Never run video watch-time heartbeats during examinations or outside stream tab
    if (activeMainTab !== 'stream') return;

    recordClassAttendanceHeartbeat(
      session,
      classSettingsRef.current?.youtubeUrl,
      classSettingsRef.current?.liveLabel,
      10,
      'heartbeat',
      true,
      'Active watching'
    );

    const heartbeatInterval = setInterval(() => {
      if (document.visibilityState === 'visible' && activeMainTab === 'stream') {
        recordClassAttendanceHeartbeat(
          session,
          classSettingsRef.current?.youtubeUrl,
          classSettingsRef.current?.liveLabel,
          30,
          'heartbeat',
          true,
          'Active watching'
        );
      }
    }, 30000);

    return () => clearInterval(heartbeatInterval);
  }, [session, activeMainTab]);

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    navigate('/class', { replace: true });
  };

  const embedUrl = classSettings?.youtubeUrl ? getYouTubeEmbedUrl(classSettings.youtubeUrl) : null;
  const isLive = Boolean(classSettings?.isLiveNow && embedUrl);

  // Alert student via desktop notification and chime when stream starts
  const prevIsLiveRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevIsLiveRef.current === false && isLive) {
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        try {
          const notif = new Notification('🔴 Live Lecture Started!', {
            body: `${classSettings?.liveLabel || 'Live lecture is now streaming'}. Click to watch!`,
            icon: '/logo192.png',
          });
          notif.onclick = () => {
            window.focus();
            setActiveMainTab('stream');
          };
        } catch {
          // Ignore
        }
      }
      playNotificationChime();
    }
    prevIsLiveRef.current = isLive;
  }, [isLive, classSettings?.liveLabel]);

  const formatScheduleDate = (rawDate: any) => {
    if (!rawDate) return 'Date TBA';
    try {
      const d = rawDate.toDate ? rawDate.toDate() : new Date(rawDate);
      return d.toLocaleDateString('en-IN', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Date TBA';
    }
  };

  if (!session) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-success-800 text-white flex flex-col selection:bg-warning-500 selection:text-white relative overflow-x-hidden">
      {/* Background Ambience Layer */}
      <div className="absolute inset-0 opacity-15 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {/* Floating sports balls in background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-12 left-6 animate-bounce opacity-20">
          <div className="w-12 h-12 bg-warning-500/80 rounded-full flex items-center justify-center text-xl">⚽</div>
        </div>
        <div className="absolute top-24 right-8 animate-bounce delay-1000 opacity-20">
          <div className="w-10 h-10 bg-success-500/80 rounded-full flex items-center justify-center text-lg">🏸</div>
        </div>
        <div className="absolute bottom-24 right-10 animate-bounce delay-500 opacity-20">
          <div className="w-12 h-12 bg-warning-500/80 rounded-full flex items-center justify-center text-xl">🏀</div>
        </div>
      </div>

      {/* Top Navbar */}
      <header className="sticky top-0 z-40 w-full border-b border-white/15 bg-primary-950/50 backdrop-blur-md px-3 sm:px-6 lg:px-8 py-2.5 sm:py-3.5">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          {/* Brand */}
          <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0">
            <img
              src="/logo192.png"
              alt="Kreed Odisha Logo"
              className="w-9 h-9 sm:w-11 sm:h-11 object-contain flex-shrink-0 drop-shadow"
              onError={(e) => {
                (e.target as HTMLElement).style.display = 'none';
              }}
            />
            <div className="flex flex-col min-w-0">
              <div className="flex items-center space-x-2">
                <span className="font-bold text-sm sm:text-base tracking-tight truncate text-white">
                  Kreed Sahayak
                </span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-warning-500 text-primary-950 flex-shrink-0">
                  Phase 2
                </span>
              </div>
              <span className="text-[10px] sm:text-xs text-blue-100/80 truncate hidden xs:inline">
                Live Classroom &amp; Attendance Portal
              </span>
            </div>
          </div>

          {/* Right Header: Candidate badge + Notification Bell + Sign Out */}
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-2.5 sm:px-3.5 py-1 sm:py-1.5 shadow-sm">
              <UserCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-warning-400 flex-shrink-0" />
              <div className="flex flex-col text-left">
                <span className="text-xs sm:text-sm font-semibold text-white max-w-[85px] xs:max-w-[120px] sm:max-w-[160px] truncate leading-tight">
                  {session.studentName}
                </span>
                <span className="text-[10px] text-blue-100 font-mono leading-none mt-0.5">
                  ID: {session.regNoSuffix}
                </span>
              </div>
            </div>

            <HeaderNotificationBell />

            <button
              onClick={handleLogout}
              className="flex items-center space-x-1 px-2.5 py-1.5 sm:px-3 rounded-lg bg-red-600/30 hover:bg-red-600/50 text-white border border-red-400/30 text-xs font-semibold transition-all backdrop-blur-sm shadow cursor-pointer"
              title="Sign Out"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="relative z-10 flex-1 w-full max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 flex flex-col gap-4 sm:gap-6">
        {/* Permission Banner for Classroom Audio & Push Alerts */}
        <NotificationPermissionBanner />

        {/* 20-Minute Upcoming Class Countdown & Chime Alert for Candidates */}
        <ClassUpcomingNotificationBanner
          schedules={classSettings?.scheduleEntries || []}
          role="candidate"
          isCurrentlyLive={isLive}
        />

        {/* Monday Special Question & Doubt Session Banner */}
        {isTodayMonday && (
          <div className="rounded-2xl p-4 sm:p-5 bg-gradient-to-r from-purple-950/80 via-indigo-950/90 to-purple-950/80 border border-purple-400/40 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xl backdrop-blur-md animate-fade-in">
            <div className="flex items-start sm:items-center space-x-3.5">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-warning-400 to-purple-600 flex items-center justify-center text-xl shadow-lg shrink-0">
                ❓
              </div>
              <div>
                <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-wider bg-warning-500 text-slate-950 uppercase shadow-sm animate-pulse">
                    MONDAY SPECIAL
                  </span>
                  <span className="text-xs font-bold text-purple-200">
                    Weekly Question &amp; Doubt Clearing Session Open!
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-blue-100/90 mt-1 max-w-2xl leading-relaxed">
                  Today is Monday! Candidates can submit up to <strong>3 priority questions</strong>. Instructors address these live during today's special doubt resolution class.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setActiveMainTab('queries')}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-warning-500 to-amber-500 hover:from-warning-400 hover:to-amber-400 text-slate-950 text-xs sm:text-sm font-black transition-all shadow-lg shadow-warning-500/25 shrink-0 self-start sm:self-auto cursor-pointer flex items-center space-x-1.5 active:scale-95"
            >
              <span>Ask Your 3 Questions</span>
              <span className="text-base">&rarr;</span>
            </button>
          </div>
        )}

        {/* Student Primary Navigation Tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-white/10 p-1.5 rounded-2xl border border-white/15 backdrop-blur-md shadow-lg">
          <button
            type="button"
            onClick={() => setActiveMainTab('stream')}
            className={`flex items-center justify-center space-x-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${activeMainTab === 'stream'
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg ring-2 ring-blue-400/50'
              : 'text-blue-100 hover:text-white hover:bg-white/10'
              }`}
          >
            <VideoCameraIcon className="w-4 h-4 shrink-0" />
            <span className="truncate">Live Class</span>
            {isLive && (
              <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse ml-1 shrink-0" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveMainTab('queries')}
            className={`flex items-center justify-center space-x-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${activeMainTab === 'queries'
              ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg ring-2 ring-indigo-400/50'
              : 'text-blue-100 hover:text-white hover:bg-white/10'
              }`}
          >
            <QuestionMarkCircleIcon className="w-4 h-4 text-warning-400 shrink-0" />
            <span className="truncate">Weekly Queries</span>
            {isTodayMonday ? (
              <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-warning-400 text-slate-950 shadow-sm animate-pulse shrink-0">
                ✨ Open Today
              </span>
            ) : (
              <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-warning-500/30 text-warning-300 border border-warning-500/40 hidden md:inline shrink-0">
                Ask 3/Wk
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveMainTab('exams')}
            className={`flex items-center justify-center space-x-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${activeMainTab === 'exams'
              ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg ring-2 ring-amber-400/50'
              : 'text-blue-100 hover:text-white hover:bg-white/10'
              }`}
          >
            <AcademicCapIcon className="w-4 h-4 text-amber-300 shrink-0" />
            <span className="truncate">Examination Portal</span>
            <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-300 border border-amber-400/40 animate-pulse hidden md:inline shrink-0">
              Exam Hall
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveMainTab('schedule')}
            className={`flex items-center justify-center space-x-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${activeMainTab === 'schedule'
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg ring-2 ring-blue-400/50'
              : 'text-blue-100 hover:text-white hover:bg-white/10'
              }`}
          >
            <CalendarIcon className="w-4 h-4 shrink-0" />
            <span className="truncate">Schedule &amp; Info</span>
          </button>
        </div>

        {/* Tab 2: Weekly Student Queries (Questions for Instructors) */}
        {activeMainTab === 'queries' && session && (
          <WeeklyQueriesStudentTab session={session} />
        )}

        {/* Tab 3: Official Examination Portal */}
        {activeMainTab === 'exams' && session && (
          <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
            <ExamPortal session={session} onLogout={handleLogout} />
          </div>
        )}

        {/* Tab 1: Live Stream or Tab 4: Schedule view */}
        {activeMainTab !== 'queries' && activeMainTab !== 'exams' && (
          <>
            {/* Live Examination Alert Banner */}
            {(getExamPortalPhase().phase === 'LOBBY_OPEN' || getExamPortalPhase().phase === 'EXAM_ACTIVE') && (
              <div className="rounded-2xl p-4 bg-gradient-to-r from-amber-950/90 via-orange-950 to-amber-950/80 border border-amber-400/60 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xl backdrop-blur-md animate-pulse mb-4">
                <div className="flex items-center space-x-3">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                  </span>
                  <span className="text-xs sm:text-sm font-black text-amber-200">
                    ⚡ Examination is LIVE! Candidates must report to the Examination Hall immediately.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveMainTab('exams')}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 text-xs font-black transition shadow-lg shrink-0 self-start sm:self-auto cursor-pointer flex items-center space-x-1.5"
                >
                  <span>Enter Examination Hall</span>
                  <span>&rarr;</span>
                </button>
              </div>
            )}
            {/* Monday Doubt Clearing Announcement Banner */}
            {isTodayMonday && (
              <div className="rounded-2xl p-4 bg-gradient-to-r from-indigo-950/80 via-purple-950/80 to-indigo-950/80 border border-indigo-400/50 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xl backdrop-blur-md mb-4">
                <div className="flex items-center space-x-3">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-indigo-200">
                    ✨ <strong>Monday Special Session:</strong> Weekly Question Asking Portal is open! Submit up to 3 doubts for today's classes.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveMainTab('queries')}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 text-white text-xs font-black transition shadow-lg shrink-0 self-start sm:self-auto cursor-pointer flex items-center space-x-1.5"
                >
                  <QuestionMarkCircleIcon className="w-4 h-4" />
                  <span>Ask Questions Now</span>
                  <span>&rarr;</span>
                </button>
              </div>
            )}

            {/* Live broadcast prompt if on schedule tab */}
            {activeMainTab === 'schedule' && isLive && (
              <div className="rounded-2xl p-4 bg-gradient-to-r from-red-950/70 via-slate-900 to-red-950/60 border border-red-500/40 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xl backdrop-blur-md">
                <div className="flex items-center space-x-3">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                  <span className="text-xs sm:text-sm font-semibold">
                    🔴 Live Classroom session is active right now!
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveMainTab('stream')}
                  className="px-3.5 py-1.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold transition shadow-lg shrink-0 self-start sm:self-auto cursor-pointer"
                >
                  Join Live Stream &rarr;
                </button>
              </div>
            )}

            {/* Video broadcast and live chat are mounted when on stream tab */}
            {activeMainTab === 'stream' && (
              <>
                {/* Status Notification Bar */}
                <div className={`rounded-2xl p-3.5 sm:p-4 border backdrop-blur-md flex flex-col xs:flex-row items-start xs:items-center justify-between gap-3 transition-all shadow-lg ${isLive
                  ? 'bg-red-950/40 border-red-400/40 text-white'
                  : 'bg-white/10 border-white/20 text-white'
                  }`}>
                  <div className="flex items-center space-x-2.5 sm:space-x-3 flex-wrap">
                    {isLive ? (
                      <>
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                        </span>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold tracking-wider bg-red-600 text-white uppercase shadow">
                          LIVE STREAM
                        </span>
                        <span className="text-xs sm:text-sm font-semibold text-white">
                          {classSettings?.liveLabel || (isTodayMonday ? 'Monday Special Question & Doubt Clearing Session' : 'Online Lecture Session is Active')}
                          {classSettings?.activeInstructorName && (
                            <span className="font-normal text-blue-200 ml-1">
                              &bull; Instructor: <strong className="text-white">{classSettings.activeInstructorName}</strong>
                            </span>
                          )}
                        </span>
                        {/* Live viewer count badge */}
                        {liveViewerCount > 0 && (
                          <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white border border-white/30 backdrop-blur-sm">
                            <EyeIcon className="w-3.5 h-3.5 text-emerald-300" />
                            <span>{liveViewerCount} watching</span>
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <SignalIcon className="w-4 h-4 text-blue-200" />
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-white/15 text-white uppercase border border-white/20">
                          OFFLINE
                        </span>
                        <span className="text-xs sm:text-sm text-blue-100 font-medium">
                          No broadcast currently live. Upcoming lecture slots are listed below.
                        </span>
                      </>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 self-end xs:self-auto">
                    {/* Live Chat Toggle Button */}
                    <button
                      type="button"
                      onClick={() => setShowChat(!showChat)}
                      className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all shadow-sm active:scale-95 cursor-pointer ${showChat
                        ? 'bg-warning-500/25 hover:bg-warning-500/35 text-warning-300 border-warning-400/40 shadow-warning-500/20'
                        : 'bg-white/15 hover:bg-white/25 text-white border-white/25'
                        }`}
                      title={showChat ? 'Hide Live Chat' : 'Open Live Chat'}
                    >
                      <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
                      <span>{showChat ? 'Hide Chat' : 'Live Chat'}</span>
                    </button>

                    {isLive && (
                      <button
                        type="button"
                        onClick={toggleFullscreen}
                        className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/90 hover:bg-emerald-600 border border-emerald-400/40 text-xs font-semibold text-white transition-all shadow-sm active:scale-95 cursor-pointer"
                        title={isFullscreen ? 'Exit Full Screen' : 'Open Full Screen'}
                      >
                        {isFullscreen ? (
                          <>
                            <ArrowsPointingInIcon className="w-3.5 h-3.5 text-warning-300" />
                            <span>Exit Full Screen</span>
                          </>
                        ) : (
                          <>
                            <ArrowsPointingOutIcon className="w-3.5 h-3.5 text-white" />
                            <span>Full Screen</span>
                          </>
                        )}
                      </button>
                    )}

                    <button
                      onClick={() => {
                        setRefreshing(true);
                        fetchSettings();
                      }}
                      disabled={refreshing}
                      className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/25 text-xs font-semibold text-white transition-all shadow-sm cursor-pointer"
                    >
                      <ArrowPathIcon className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                      <span>{refreshing ? 'Checking...' : 'Refresh'}</span>
                    </button>
                  </div>
                </div>

                {/* Main Live Broadcast & Custom Chat Section */}
                <div className={`grid grid-cols-1 ${showChat && !isFullscreen ? 'lg:grid-cols-12' : ''} gap-4 sm:gap-6 items-start`}>
                  {/* Video Player Box */}
                  <div className={showChat && !isFullscreen ? 'lg:col-span-8' : 'w-full'}>
                    <section
                      ref={playerContainerRef}
                      className={`w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl relative transition-all ${isFullscreen ? 'fixed inset-0 z-50 rounded-none border-0 w-screen h-screen bg-black flex flex-col items-center justify-center' : ''
                        }`}
                    >
                      {isLive && embedUrl ? (
                        <div className={`relative w-full ${isFullscreen ? 'h-full flex flex-col items-center justify-center bg-black' : ''}`}>
                          {/* External Full Screen Floating Button directly on/next to the viewer */}
                          <div className="absolute top-3 right-3 z-30 flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={toggleFullscreen}
                              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-black/80 hover:bg-black text-white text-xs font-semibold backdrop-blur-md border border-white/30 shadow-xl transition-all active:scale-95 cursor-pointer"
                              title={isFullscreen ? 'Exit Full Screen' : 'Open Full Screen'}
                            >
                              {isFullscreen ? (
                                <>
                                  <ArrowsPointingInIcon className="w-4 h-4 text-warning-400" />
                                  <span>Exit Fullscreen</span>
                                </>
                              ) : (
                                <>
                                  <ArrowsPointingOutIcon className="w-4 h-4 text-emerald-400" />
                                  <span>Full Screen</span>
                                </>
                              )}
                            </button>
                          </div>

                          {/* 16:9 Aspect ratio video frame */}
                          <div
                            className={`relative w-full bg-black select-none ${isFullscreen ? 'h-full max-h-screen w-full max-w-full aspect-video flex items-center justify-center' : 'pt-[56.25%]'
                              }`}
                          >
                            <iframe
                              src={embedUrl}
                              title="Kreed Sahayak Live Broadcast"
                              className="absolute inset-0 w-full h-full border-0 pointer-events-none select-none"
                              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                              tabIndex={-1}
                            />
                            {/* Click-Shield Overlay: completely prevents clicks, right-clicks, and drag events from reaching YouTube */}
                            <div
                              className="absolute inset-0 z-20 bg-transparent select-none cursor-default"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onDoubleClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleFullscreen();
                              }}
                              title="Live Stream Viewer (Direct interaction disabled for link protection)"
                              aria-hidden="true"
                            />
                          </div>

                          {/* Bottom protection note and quick fullscreen link */}
                          {!isFullscreen && (
                            <div className="px-4 py-2.5 bg-black/40 border-t border-white/10 flex items-center justify-between text-xs text-blue-200">
                              <span className="flex items-center space-x-1.5">
                                <LockClosedIcon className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                                <span>Stream link protected • Direct player clicks disabled</span>
                              </span>
                              <button
                                type="button"
                                onClick={toggleFullscreen}
                                className="flex items-center space-x-1 text-emerald-300 hover:text-emerald-200 font-medium transition-colors cursor-pointer"
                              >
                                <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                                <span>External Full Screen</span>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="px-4 py-12 sm:py-16 text-center flex flex-col items-center justify-center">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center text-warning-400 mb-4 shadow-xl">
                            <VideoCameraIcon className="w-8 h-8 sm:w-10 sm:h-10" />
                          </div>
                          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                            Live Broadcast Screen
                          </h2>
                          <p className="text-xs sm:text-sm text-blue-100 max-w-md mx-auto leading-relaxed mb-6">
                            When the instructor starts streaming on YouTube via OBS Studio, the live feed will automatically play on this screen.
                          </p>

                          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-left w-full max-w-md space-y-2.5 text-xs text-white shadow-inner">
                            <div className="flex items-center space-x-2">
                              <CheckBadgeIcon className="w-4 h-4 text-success-300 flex-shrink-0" />
                              <span>Attendance is recorded automatically while viewing.</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <CheckBadgeIcon className="w-4 h-4 text-success-300 flex-shrink-0" />
                              <span>Keep this tab active on your phone or computer during the session.</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <CheckBadgeIcon className="w-4 h-4 text-success-300 flex-shrink-0" />
                              <span>Check class alerts on your official Telegram group.</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </section>
                  </div>

                  {/* Custom Live Classroom Chat (Hidden in Fullscreen) */}
                  {!isFullscreen && showChat && (
                    <div className="lg:col-span-4 w-full">
                      <ClassChatBox
                        currentUser={{
                          name: session?.studentName || 'Student',
                          id: session?.regNoSuffix || '000',
                          role: 'student',
                          badge: `ID: ${session?.regNoSuffix || '000'}${session?.batchLabel ? ` • ${session.batchLabel}` : ''}`,
                        }}
                        isInstructor={false}
                        theme="dark-blue"
                        maxHeightClass="h-[480px] sm:h-[520px] lg:h-[490px] xl:h-[540px]"
                        viewerCount={liveViewerCount}
                        youtubeUrl={classSettings?.youtubeUrl}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Schedule & Notes Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Schedule Card */}
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sm:p-6 shadow-xl">
                <div className="flex items-center space-x-2.5 pb-3 mb-4 border-b border-white/15">
                  <CalendarIcon className="w-5 h-5 text-warning-400" />
                  <h3 className="font-bold text-sm sm:text-base text-white">Class Schedule</h3>
                </div>

                {loading ? (
                  <p className="text-xs text-blue-200 py-6 text-center">Loading schedule slots...</p>
                ) : classSettings?.scheduleEntries && classSettings.scheduleEntries.length > 0 ? (
                  <div className="space-y-3">
                    {classSettings.scheduleEntries.map((entry: ClassScheduleEntry) => (
                      <div
                        key={entry.id}
                        className="p-3.5 rounded-xl bg-white/10 border border-white/15 flex flex-col gap-1.5 hover:bg-white/15 transition-all shadow-sm"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-xs sm:text-sm text-white">
                            {entry.title}
                          </span>
                          {entry.batchLabel && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-warning-500 text-primary-950 whitespace-nowrap shadow-sm">
                              {entry.batchLabel}
                            </span>
                          )}
                        </div>
                        {entry.topic && (
                          <p className="text-[11px] sm:text-xs text-blue-100">
                            Topic: {entry.topic}
                          </p>
                        )}
                        <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-white/10 text-[11px] text-blue-200">
                          <span className="flex items-center space-x-1.5">
                            <ClockIcon className="w-3.5 h-3.5 text-warning-300" />
                            <span>{formatScheduleDate(entry.scheduledAt)}</span>
                          </span>
                          <div className="flex items-center space-x-2">
                            {entry.instructorName && (
                              <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-500/30 text-indigo-100 font-medium border border-indigo-400/30">
                                👤 {entry.instructorName}
                              </span>
                            )}
                            {entry.durationMinutes && (
                              <span className="text-[10px] px-2 py-0.5 rounded bg-white/15 font-semibold text-white">
                                {entry.durationMinutes} mins
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-xs text-blue-200">
                    No upcoming sessions scheduled yet. Check back soon.
                  </div>
                )}
              </div>

              {/* Guidelines Card */}
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sm:p-6 shadow-xl">
                <div className="flex items-center space-x-2.5 pb-3 mb-4 border-b border-white/15">
                  <ExclamationCircleIcon className="w-5 h-5 text-warning-400" />
                  <h3 className="font-bold text-sm sm:text-base text-white">Student Guidelines</h3>
                </div>
                <ul className="space-y-3 text-xs text-blue-100 list-disc pl-4 leading-relaxed">
                  <li>
                    <strong className="text-white">Mandatory Attendance:</strong> Attending online theoretical modules on Kreed Sahayak Portal is required for Second Phase evaluation.
                  </li>
                  <li>
                    <strong className="text-white">Active Watch Tracking:</strong> Your watch duration is recorded in 30-second increments and forwarded directly to the admin training console.
                  </li>
                  <li>
                    <strong className="text-white">Audio Setup:</strong> Use earphones or keep your mobile volume loud to clearly follow the lecturer's instructions.
                  </li>
                  <li>
                    <strong className="text-white">Weekly Queries:</strong> Use the "Weekly Queries" tab to submit up to 3 questions per week directly to your instructors.
                  </li>
                  <li>
                    <strong className="text-white">Support &amp; Community:</strong> Use the dedicated Telegram group to raise questions and receive immediate broadcasts.
                  </li>
                </ul>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-4 px-4 text-center border-t border-white/15 text-[11px] text-blue-100/70">
        <p>&copy; {new Date().getFullYear()} Kreed Odisha Sports Excellence Programme &bull; Kreed Sahayak Portal &bull; okreed.in</p>
      </footer>
    </div>
  );
};

export default ClassDashboardPage;
